import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { InferResponseType } from 'hono/client';
import { ExternalLink, MessageSquare, Mic } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '~/components/ui/table';
import { api } from '~/lib/api';
import { meQueryOptions } from '~/lib/auth';
import { departmentsQueryOptions } from '~/lib/departments';
import { formatDate } from '~/utils/date';

type InterviewsResponse = InferResponseType<typeof api.api.admin.interviews.$get>;
type SessionEntry = InterviewsResponse['sessions'][number];

type InterviewsListProps = {
  sessions: SessionEntry[];
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
};

export function InterviewsList({
  sessions,
  total,
  page,
  limit,
  onPageChange
}: InterviewsListProps): React.ReactNode {
  const { t } = useTranslation();
  const totalPages = Math.ceil(total / limit);

  if (sessions.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
        {t('admin.interviews.noSessions')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="min-w-24 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.interviews.columns.org')}
            </TableHead>
            <TableHead className="min-w-32 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.interviews.columns.job')}
            </TableHead>
            <TableHead className="min-w-32 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.interviews.columns.candidate')}
            </TableHead>
            <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.interviews.columns.channel')}
            </TableHead>
            <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.interviews.columns.language')}
            </TableHead>
            <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.interviews.columns.status')}
            </TableHead>
            <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.interviews.columns.processing')}
            </TableHead>
            <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.interviews.columns.outcome')}
            </TableHead>
            <TableHead className="min-w-36 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.interviews.columns.updatedAt')}
            </TableHead>
            <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sessions.map((entry) => (
            <SessionRow key={entry.sessionId} entry={entry} />
          ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {(page - 1) * limit + 1}–{Math.min(page * limit, total)} / {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => {
                onPageChange(page - 1);
              }}
            >
              {t('pagination.previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => {
                onPageChange(page + 1);
              }}
            >
              {t('pagination.next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SessionStatusBadge({
  status
}: {
  status: SessionEntry['sessionStatus'];
}): React.ReactNode {
  const { t } = useTranslation();

  const variantMap: Record<
    SessionEntry['sessionStatus'],
    'default' | 'secondary' | 'destructive' | 'outline'
  > = {
    completed: 'default',
    in_progress: 'secondary',
    failed: 'destructive',
    restarted: 'outline',
    abandoned: 'outline'
  };

  return (
    <Badge variant={variantMap[status]}>{t(`admin.interviews.sessionStatus.${status}`)}</Badge>
  );
}

function ScoringStatusBadge({
  status
}: {
  status: SessionEntry['scoringStatus'];
}): React.ReactNode {
  const { t } = useTranslation();

  if (status === null) return <span className="text-muted-foreground">—</span>;

  const variantMap: Record<
    NonNullable<SessionEntry['scoringStatus']>,
    'default' | 'secondary' | 'destructive' | 'outline'
  > = {
    completed: 'secondary',
    pending: 'outline',
    processing: 'outline',
    failed: 'destructive'
  };

  return (
    <Badge variant={variantMap[status]}>{t(`admin.interviews.scoringStatus.${status}`)}</Badge>
  );
}

function OutcomeBadge({ outcome }: { outcome: SessionEntry['outcome'] }): React.ReactNode {
  const { t } = useTranslation();

  if (outcome === null) return <span className="text-muted-foreground">—</span>;

  if (outcome === 'pass') {
    return (
      <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
        {t('admin.interviews.outcome.pass')}
      </Badge>
    );
  }

  if (outcome === 'fail') {
    return <Badge variant="destructive">{t('admin.interviews.outcome.fail')}</Badge>;
  }

  return <Badge variant="secondary">{t('admin.interviews.outcome.incomplete')}</Badge>;
}

function SessionRow({ entry }: { entry: SessionEntry }): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const switchOrgMutation = useMutation({
    mutationFn: async (orgId: string) => {
      const res = await api.api.admin.organizations.switch.$post({ json: { orgId } });
      if (!res.ok) throw new Error('Failed to switch organization');
      return res.json();
    },
    onSuccess: async () => {
      // Await both invalidations so the org context (session + departments) is fully
      // refreshed before navigating — this ensures the admin banner appears immediately
      // and the target page loads under the switched org, not the previous one.
      await queryClient.invalidateQueries({ queryKey: meQueryOptions.queryKey });
      await queryClient.invalidateQueries({ queryKey: departmentsQueryOptions.queryKey });
      void navigate({
        to: '/org/$orgSlug/department/$deptSlug/jobs/$jobId/$applicationId',
        params: {
          orgSlug: entry.orgSlug,
          deptSlug: entry.deptSlug,
          jobId: entry.jobId,
          applicationId: entry.applicationId
        }
      });
    }
  });

  return (
    <TableRow>
      <TableCell className="max-w-32 py-3 text-sm text-muted-foreground">
        <span className="block truncate" title={entry.orgName}>
          {entry.orgName}
        </span>
      </TableCell>
      <TableCell className="max-w-40 py-3 text-sm text-muted-foreground">
        <span className="block truncate" title={entry.jobTitle}>
          {entry.jobTitle}
        </span>
      </TableCell>
      <TableCell className="max-w-36 py-3 text-sm text-muted-foreground">
        <span className="block truncate" title={entry.candidateName}>
          {entry.candidateName}
        </span>
      </TableCell>
      <TableCell className="py-3">
        <Badge variant="secondary" className="flex w-fit items-center gap-1">
          {entry.modality === 'voice' ? (
            <Mic className="size-3" />
          ) : (
            <MessageSquare className="size-3" />
          )}
          {entry.modality === 'voice'
            ? t('admin.interviews.filter.voice')
            : t('admin.interviews.filter.chat')}
        </Badge>
      </TableCell>
      <TableCell className="py-3 text-sm text-muted-foreground">
        {entry.language !== null ? entry.language.toUpperCase() : '—'}
      </TableCell>
      <TableCell className="py-3">
        <SessionStatusBadge status={entry.sessionStatus} />
      </TableCell>
      <TableCell className="py-3">
        <ScoringStatusBadge status={entry.scoringStatus} />
      </TableCell>
      <TableCell className="py-3">
        <OutcomeBadge outcome={entry.outcome} />
      </TableCell>
      <TableCell className="py-3 text-sm text-muted-foreground">
        {formatDate(entry.updatedAt)}
      </TableCell>
      <TableCell className="py-3">
        <Button
          variant="ghost"
          size="icon"
          disabled={switchOrgMutation.isPending}
          onClick={() => {
            switchOrgMutation.mutate(entry.orgId);
          }}
        >
          <ExternalLink className="size-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
